import parsePhoneNumberFromString from 'libphonenumber-js'
import { z } from 'zod'
import { isValidEmailAddress, normalizeEmailAddress } from './emailIdentity.ts'

/**
 * The account itself is identified by an opaque UUIDv7. Everything else is an **identity**:
 * a way of reaching the account, or a way of proving it is yours, attached to it as one of
 * a list rather than occupying a fixed slot.
 *
 * The two kinds are not interchangeable. An **address** — an email address, a phone number
 * — is what somebody types to write to you, and it is what appears in a URL. A **proof** —
 * a Google subject today, other providers later — says who is asking and nothing more:
 * nobody can write a message to a Google subject.
 *
 * An identity therefore belongs to an account and never is one. The account outlives any
 * particular identity, and gains and loses them over time.
 */
export const ACCOUNT_IDENTITY_PROVIDERS = ['email', 'phone', 'google'] as const
export type AccountIdentityProvider = (typeof ACCOUNT_IDENTITY_PROVIDERS)[number]

export type AccountIdentity = {
  provider: AccountIdentityProvider
  /** Normalized: a lowercased address, a phone number in E.164, or a provider subject. */
  subject: string
}

/**
 * Whether other people can reach an account through this kind of identity.
 *
 * Written down once, here, because everything downstream depends on it: which identity may
 * appear in a URL, which one a notification is sent to, and which one somebody may type to
 * start a conversation. A provider that only proves who you are is never any of those.
 */
export function isAddressableProvider(provider: AccountIdentityProvider): boolean {
  return provider === 'email' || provider === 'phone'
}

/**
 * A handle is the addressable subset of the identities, in the vocabulary the interface and
 * the URL use. It is deliberately narrower than an identity: `parseAccountHandle` accepts
 * an email address or an international phone number and nothing else.
 */
export type AccountHandleKind = 'email' | 'phone'

export type AccountHandle = {
  kind: AccountHandleKind
  /** Normalized: a lowercased email address, or a phone number in E.164 form. */
  value: string
}


export const MAXIMUM_PHONE_NUMBER_LENGTH = 20

/**
 * Phone numbers are always stored in E.164 (`+5511999991234`), which is the one form that
 * is unambiguous worldwide. Anything that cannot be resolved to a valid international
 * number is rejected rather than guessed at: guessing a country would silently address a
 * different person.
 */
export function normalizePhoneNumber(
  rawPhoneNumber: string,
  defaultCallingCode: string | null = null,
): string | null {
  const trimmedPhoneNumber = rawPhoneNumber.trim()
  const internationalPhoneNumber = trimmedPhoneNumber.startsWith('+')
    ? trimmedPhoneNumber
    : applyDefaultCallingCode(trimmedPhoneNumber, defaultCallingCode)

  if (internationalPhoneNumber === null) {
    return null
  }

  const parsedPhoneNumber = parsePhoneNumberFromString(internationalPhoneNumber)
  if (parsedPhoneNumber === undefined || !parsedPhoneNumber.isValid()) {
    return null
  }
  return parsedPhoneNumber.number
}

/**
 * Completes a number that was typed without a country code.
 *
 * The national trunk prefix is dropped first: somebody in Brazil writes `011 99999-1234`
 * for the number that is `+55 11 99999-1234` internationally, and pasting the country code
 * in front of the zero would produce a different number entirely. Whatever comes out is
 * still validated like any other number, so a wrong guess is rejected rather than stored.
 */
function applyDefaultCallingCode(
  trimmedPhoneNumber: string,
  defaultCallingCode: string | null,
): string | null {
  if (defaultCallingCode === null || trimmedPhoneNumber.length === 0) {
    return null
  }

  const nationalDigits = trimmedPhoneNumber.replace(/[^0-9]/g, '').replace(/^0+/, '')
  return nationalDigits.length === 0 ? null : `${defaultCallingCode}${nationalDigits}`
}

export function isValidPhoneNumber(
  candidatePhoneNumber: string,
  defaultCallingCode: string | null = null,
): boolean {
  return normalizePhoneNumber(candidatePhoneNumber, defaultCallingCode) !== null
}

export const normalizedPhoneNumberSchema = z
  .string()
  .max(MAXIMUM_PHONE_NUMBER_LENGTH)
  .transform((rawPhoneNumber) => normalizePhoneNumber(rawPhoneNumber))
  .refine((normalizedPhoneNumber) => normalizedPhoneNumber !== null, {
    message: 'A phone number must be a valid international number, such as +5511999991234.',
  })
  .transform((normalizedPhoneNumber) => normalizedPhoneNumber as string)

/**
 * Reads whatever a person typed as the address of an account. A leading `+` means it can
 * only be a phone number, so it is never mistaken for a malformed email address.
 */
/** Digits and the punctuation people write numbers with: never an email address. */
const LOOKS_LIKE_A_NATIONAL_PHONE_NUMBER = /^[0-9()\s.-]+$/

export function parseAccountHandle(
  rawHandle: string,
  defaultCallingCode: string | null = null,
): AccountHandle | null {
  const trimmedHandle = rawHandle.trim()
  if (trimmedHandle.length === 0) {
    return null
  }

  if (trimmedHandle.startsWith('+')) {
    const normalizedPhoneNumber = normalizePhoneNumber(trimmedHandle)
    return normalizedPhoneNumber === null ? null : { kind: 'phone', value: normalizedPhoneNumber }
  }

  if (defaultCallingCode !== null && LOOKS_LIKE_A_NATIONAL_PHONE_NUMBER.test(trimmedHandle)) {
    const normalizedPhoneNumber = normalizePhoneNumber(trimmedHandle, defaultCallingCode)
    return normalizedPhoneNumber === null ? null : { kind: 'phone', value: normalizedPhoneNumber }
  }

  return isValidEmailAddress(trimmedHandle)
    ? { kind: 'email', value: normalizeEmailAddress(trimmedHandle) }
    : null
}

export function isValidAccountHandle(rawHandle: string): boolean {
  return parseAccountHandle(rawHandle) !== null
}

/** Zod counterpart, for request bodies and path parameters. */
export const accountHandleSchema = z
  .string()
  .max(320)
  .transform((rawHandle) => parseAccountHandle(rawHandle))
  .refine((parsedHandle) => parsedHandle !== null, {
    message: 'An account is addressed by an email address or an international phone number.',
  })
  .transform((parsedHandle) => parsedHandle as AccountHandle)

/**
 * Handles appear in the path, so that a browser tab shows which account it belongs to and
 * two accounts can be open side by side. `@ . + - _` are all legal path characters, and
 * leaving them alone is what keeps `/amanda@gmail.com/chats` readable.
 */
const PATH_SAFE_HANDLE_CHARACTERS = /[A-Za-z0-9@.+\-_]/

export function encodeAccountHandleForPath(handleValue: string): string {
  return [...handleValue]
    .map((handleCharacter) =>
      PATH_SAFE_HANDLE_CHARACTERS.test(handleCharacter)
        ? handleCharacter
        : encodeURIComponent(handleCharacter),
    )
    .join('')
}

export function decodeAccountHandleFromPath(encodedHandle: string): string {
  return decodeURIComponent(encodedHandle)
}

/** The addressable identities, in the order they are preferred as a handle. */
const HANDLE_PREFERENCE: readonly AccountIdentityProvider[] = ['email', 'phone']

/**
 * Which identity stands for the account: in its URL, in the interface, and wherever a
 * person has no display name.
 *
 * The email address wins when there is one, because it is what people recognize. A proof
 * is never a candidate — an account known only through Google has no handle, which is why
 * signing in with Google also records the address it comes with.
 */
export function findAccountHandle(identities: readonly AccountIdentity[]): string | null {
  for (const preferredProvider of HANDLE_PREFERENCE) {
    const matchingIdentity = identities.find(
      (identity) => identity.provider === preferredProvider,
    )
    if (matchingIdentity !== undefined) {
      return matchingIdentity.subject
    }
  }
  return null
}

/** The same, for the places that cannot carry on without one. */
export function resolveAccountHandle(identities: readonly AccountIdentity[]): string {
  const resolvedHandle = findAccountHandle(identities)
  if (resolvedHandle === null) {
    throw new Error('An account always has at least one address to be reached at.')
  }
  return resolvedHandle
}

/** The addressable identities only, which is what may be shown, shared or written to. */
export function listAddressableIdentities(
  identities: readonly AccountIdentity[],
): AccountIdentity[] {
  return identities.filter((identity) => isAddressableProvider(identity.provider))
}

/** A handle is an identity in the narrower vocabulary; this converts between the two. */
export function toAccountIdentity(handle: AccountHandle): AccountIdentity {
  return { provider: handle.kind, subject: handle.value }
}
